import React, { useState, useMemo } from 'react';
import { SearchIcon, PencilIcon, BarChart3Icon, UsersIcon, InboxIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryIcon, getCategoryLabel } from '../data/communications';
import { CHANNEL_ICONS, CHANNEL_LABELS } from '../data/channels';
import { IconTooltip } from './IconTooltip';
import type { Notice, NoticeStatus } from '../types/notice';
interface NoticeListProps {
  notices: Notice[];
  onCreate: () => void;
  onEdit: (notice: Notice) => void;
  onViewStats: (notice: Notice) => void;
}
export function NoticeList({
  notices,
  onCreate,
  onEdit,
  onViewStats
}: NoticeListProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [status, setStatus] = useState<NoticeStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const statusMeta: Record<NoticeStatus, {
    label: string;
    className: string;
    dot: string;
  }> = {
    sent: {
      label: t('Envoyé', 'Sent'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      dot: 'bg-emerald-500'
    },
    scheduled: {
      label: t('Programmé', 'Scheduled'),
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      dot: 'bg-amber-500'
    },
    draft: {
      label: t('Brouillon', 'Draft'),
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      dot: 'bg-slate-400'
    }
  };

  /** Day only in the list; the exact time is kept for the hover title. */
  const dayOf = (iso: string) => new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
  const filters: {
    id: NoticeStatus | 'all';
    label: string;
  }[] = [{
    id: 'all',
    label: t('Tous', 'All')
  }, {
    id: 'sent',
    label: t('Envoyés', 'Sent')
  }, {
    id: 'scheduled',
    label: t('Programmés', 'Scheduled')
  }, {
    id: 'draft',
    label: t('Brouillons', 'Drafts')
  }];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notices.filter(n => status === 'all' ? true : n.status === status).filter(n => q ? n.title.toLowerCase().includes(q) || getCategoryLabel(n.category)[lang].toLowerCase().includes(q) : true).sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [notices, status, query, lang]);
  const countFor = (id: NoticeStatus | 'all') => id === 'all' ? notices.length : notices.filter(n => n.status === id).length;
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-4467" data-mp="3i3d2qjogoqx">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-id="element-4468" data-mp="3k6a3uj8zi55">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4469" data-mp="22suz0ipg97lx">
          {filters.map(f => {
          const active = status === f.id;
          return <button key={f.id} onClick={() => setStatus(f.id)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-4470" data-mp="25em08nrriv41">
                {f.label}
                <span className={`ml-2 text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-4471" data-mp="1mim6b7f85hht">
                  {countFor(f.id)}
                </span>
              </button>;
        })}
        </div>

        <div className="flex items-center gap-3" data-id="element-4472" data-mp="21e22pj3a1phd">
          <div className="relative flex-1 lg:w-72" data-id="element-4473" data-mp="21bpckind88t8">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4474" data-mp="37ssyj8djmk4j" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un avis…', 'Search notices…')} aria-label={t('Rechercher un avis', 'Search notices')} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-4475" data-mp="3cre13eg9pfw5" />
          </div>
          <button onClick={onCreate} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-4476" data-mp="3tccye3jg5ve8">
            {t('Créer un avis', 'Create a notice')}
          </button>
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 px-6 text-center" data-id="element-4477" data-mp="gv8188o7z914">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-4478" data-mp="bwwpfsed6og9">
            <InboxIcon className="w-5 h-5 text-slate-400" data-id="element-4479" data-mp="vi6ri0bcbxe9" />
          </div>
          <p className="text-sm font-medium text-slate-600" data-id="element-4480" data-mp="zk1owtq5hqmr">
            {t('Aucun avis ne correspond à ces critères.', 'No notice matches these criteria.')}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-4481" data-mp="lk0n9c22af68">
            {t('Ajustez la recherche ou créez un nouvel avis.', 'Adjust your search or create a new notice.')}
          </p>
        </div> : <ul className="space-y-2" data-id="element-4482" data-mp="glpivgujcwve">
          {visible.map(notice => {
        const meta = statusMeta[notice.status];
        const CategoryIcon = getCategoryIcon(notice.category);
        return <li key={notice.id} onClick={() => onEdit(notice)} className="group bg-white rounded-xl border border-slate-200 px-5 py-4 hover:border-[#4F46E5]/40 hover:shadow-sm transition-all cursor-pointer" data-id="element-4483" data-mp="7txb5dwwc1ui">
                <div className="flex items-center gap-3.5" data-id="element-4484" data-mp="3av7a1ajaz7js">
                  {/* Status + category icon on one line, channels underneath. */}
                  <div className="hidden sm:flex flex-col gap-1.5 w-[9rem] shrink-0" data-id="element-4485" data-mp="363603v9lxswx">
                    <div className="flex items-center gap-2" data-id="element-4486" data-mp="316fkf91pszc1">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border w-fit ${meta.className}`} data-id="element-4487" data-mp="2w6t7z1tpnn4a">
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-4488" data-mp="3ovi1aeoiyo5h" />
                        {meta.label}
                      </span>
                      <IconTooltip label={getCategoryLabel(notice.category)[lang]} data-id="element-4489" data-mp="3jukji37ffw4i">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-50 border border-slate-200 text-slate-500" data-id="element-4490" data-mp="3ew983o1hhdk6">
                          <CategoryIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4491" data-mp="23hvpaxll7ob0" />
                        </span>
                      </IconTooltip>
                    </div>
                    <span className="inline-flex items-center gap-1.5" data-id="element-4492" data-mp="ixrybmdqt7zq">
                      {notice.channels.map(c => {
                  const Icon = CHANNEL_ICONS[c];
                  return <IconTooltip key={c} label={CHANNEL_LABELS[c][lang]} data-id="element-4493" data-mp="sw7sjy82gxdg">
                            <Icon className="w-3.5 h-3.5 text-slate-400 hover:text-[#4F46E5] transition-colors" aria-hidden="true" data-id="element-4494" data-mp="xqu2ry2ewjvy" />
                          </IconTooltip>;
                })}
                    </span>
                  </div>

                  <div className="min-w-0 flex-1" data-id="element-4495" data-mp="1gmk6pqtwnqch">
                    <h3 className="text-base font-semibold text-[#0F172A] truncate" data-id="element-4496" data-mp="1gl94hvg3yqnh">
                      {notice.title}
                    </h3>

                    <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-slate-500" data-id="element-4497" data-mp="3ig1ceoyh6h4l">
                      {notice.status !== 'draft' && <>
                          <span data-id="element-4498" data-mp="1j8ktn7a8cyuh">
                            {t('Du ', 'From ')}
                            <IconTooltip label={timeOf(notice.date)} data-id="element-4499" data-mp="1xwo4atz7wypm">
                              <span className="font-medium text-slate-700" data-id="element-4500" data-mp="1xz0h6sq4civt">
                                {dayOf(notice.date)}
                              </span>
                            </IconTooltip>
                            {notice.endDate && <>
                                {t(' au ', ' to ')}
                                <IconTooltip label={timeOf(notice.endDate)} data-id="element-4501" data-mp="1f6pwmezc17mi">
                                  <span className="font-medium text-slate-700" data-id="element-4502" data-mp="1a7d1z827yw9d">
                                    {dayOf(notice.endDate)}
                                  </span>
                                </IconTooltip>
                              </>}
                          </span>
                          <span aria-hidden="true" className="text-slate-300" data-id="element-4503" data-mp="2t5hup0kbhklb">
                            ·
                          </span>
                        </>}
                      <IconTooltip label={t('Personnes ciblées', 'People targeted')} data-id="element-4504" data-mp="2k5uuuj78kmud">
                        <span className="inline-flex items-center gap-1.5" data-id="element-4505" data-mp="2o6y6eti93wps">
                          <UsersIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4506" data-mp="2o90zjmepa5yq" />
                          <span className="font-medium text-slate-700" data-id="element-4507" data-mp="3bx2cd7iww8jy">
                            {notice.audience.count.toLocaleString(locale)}
                          </span>
                        </span>
                      </IconTooltip>
                    </div>
                  </div>

                  {/* Revealed on hover — kept reachable by keyboard at all times. */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4508" data-mp="3uubumlci3fzm">
                    <button onClick={e => {
                e.stopPropagation();
                onEdit(notice);
              }} aria-label={t(`Modifier « ${notice.title} »`, `Edit “${notice.title}”`)} title={t('Modifier', 'Edit')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4509" data-mp="3ik2d09euixp">
                      <PencilIcon className="w-4 h-4" data-id="element-4510" data-mp="3uzw43zzj8nji" />
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                onViewStats(notice);
              }} aria-label={t(`Statistiques de « ${notice.title} »`, `Statistics for “${notice.title}”`)} title={t('Statistiques', 'Statistics')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4511" data-mp="3gazwu55avejy">
                      <BarChart3Icon className="w-4 h-4" data-id="element-4512" data-mp="2wnd8x8zg3b3m" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}